// Waveform math for the power factor illustrations. Unit amplitudes throughout: v = sin(θ),
// i = sin(θ − φ), p = v·i. φ > 0 means current lags voltage (inductive), φ < 0 leads (capacitive).

export type PFMode = 'unity' | 'lagging' | 'leading';

/** Signed phase angle in radians: + lagging, − leading, 0 at unity. */
export const phaseAngle = (pf: number, mode: PFMode): number => {
  if (mode === 'unity') return 0;
  const phi = Math.acos(Math.min(Math.max(pf, 0), 1));
  return mode === 'lagging' ? phi : -phi;
};

export interface WavePoint { theta: number; v: number; i: number; p: number; }

/** `cycles` periods sampled at `n` points; `offset` (radians) slides the window for animation. */
export function sampleWave(phi: number, cycles = 2, n = 240, offset = 0): WavePoint[] {
  const pts: WavePoint[] = [];
  for (let k = 0; k <= n; k++) {
    const theta = (k / n) * cycles * 2 * Math.PI + offset;
    const v = Math.sin(theta);
    const i = Math.sin(theta - phi);
    pts.push({ theta, v, i, p: v * i });
  }
  return pts;
}

export interface WaveStats {
  phiDeg: number;               // signed: + lagging, − leading
  pf: number;
  avgPower: number;             // mean of p over a cycle (= cos φ / 2 for unit amplitudes)
  peakPower: number;            // max of p (= (1 + cos φ) / 2)
  returnedEnergyPct: number;    // negative area ÷ positive area: energy sent back to the source each cycle
  negativeTimePct: number;      // share of each cycle where p < 0 (= |φ| / π)
}

/* Closed forms. p(θ) = ½[cos φ − cos(2θ − φ)], so p < 0 for |φ|/π of each cycle. Positive area
   A+ = ½[cos φ·(π − |φ|) + sin|φ|] per half-period and negative area A− = ½[sin|φ| − |φ|·cos φ]. */
export function waveStats(phi: number): WaveStats {
  const a = Math.abs(phi);
  const c = Math.cos(a), s = Math.sin(a);
  const pos = 0.5 * (c * (Math.PI - a) + s);
  const neg = 0.5 * (s - a * c);
  return {
    phiDeg: (phi * 180) / Math.PI,
    pf: c,
    avgPower: c / 2,
    peakPower: (1 + c) / 2,
    returnedEnergyPct: pos > 0 ? (neg / pos) * 100 : 0,
    negativeTimePct: (a / Math.PI) * 100,
  };
}

export const MODE_COPY: Record<PFMode, { title: string; caption: string; detail: string }> = {
  unity: {
    title: 'Unity power factor',
    caption: 'Voltage and current in phase',
    detail: 'Every bit of current does real work. Power never goes negative: nothing is returned to the utility. Typical of resistive loads such as heaters and incandescent lighting.',
  },
  lagging: {
    title: 'Lagging power factor',
    caption: 'Voltage peaks before current',
    detail: 'Inductive loads (motors, transformers) make current lag voltage. For part of every cycle power is negative — energy shuttles back to the utility as reactive power, so the wiring carries more current than the work requires.',
  },
  leading: {
    title: 'Leading power factor',
    caption: 'Current peaks before voltage',
    detail: 'Capacitive loads make current lead voltage. Capacitors are added to cancel a lagging load; too much capacitance over-corrects into leading PF, which wastes capacity the same way and can raise voltage at light load.',
  },
};
